// src/components/RoleSwitcher.jsx
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

/**
 * RoleSwitcher
 * -------------
 * Deux modes d'affichage contrôlés par la prop `mode` :
 *  - "gate"    : écran plein-page bloquant, affiché par ProtectedRoute
 *                quand requiresRoleSelection === true (backend). Appelle
 *                onSwitched(redirectPath) une fois le rôle choisi, pour
 *                laisser ProtectedRoute décider de la navigation.
 *  - "compact" : petit sélecteur (header LayoutStaff/LayoutEtudiant)
 *                permettant de changer de contexte à tout moment.
 *
 * Toute la donnée (rolesDisponibles, activeRole, requiresRoleSelection) et
 * l'appel réseau (/switch-role) viennent de useAuth() — ce composant ne
 * fait plus son propre fetch, pour ne jamais diverger de l'état
 * d'authentification global.
 */
export default function RoleSwitcher({ mode = 'gate', onSwitched }) {
  const { rolesDisponibles, activeRole, requiresRoleSelection, switchRole, logout } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(false); // pour le mode compact (dropdown)

  const choisirRole = async (roleInfo) => {
    setSwitching(true);
    setErreur(null);
    try {
      const result = await switchRole(roleInfo.role, roleInfo.clientId);
      if (!result.success) {
        setErreur(result.error || 'Impossible de changer de rôle');
        return;
      }
      setOuvert(false);
      if (onSwitched) onSwitched(result.redirectPath);
    } finally {
      setSwitching(false);
    }
  };

  const libelleRole = (roleInfo) => {
    const noms = {
      SUPERADMIN: 'Super Administrateur',
      ADMIN: 'Administrateur',
      CLIENT: 'Client',
      ETUDIANT: 'Étudiant',
    };
    const base = noms[roleInfo.role] || roleInfo.role;
    return roleInfo.clientNom ? `${base} — ${roleInfo.clientNom}` : base;
  };

  const roles = rolesDisponibles || [];

  // ---- Mode "gate" : ne s'affiche que si le backend l'exige réellement ----
  if (mode === 'gate') {
    if (!requiresRoleSelection) return null;

    return (
      <div style={styles.gateOverlay}>
        <div style={styles.card}>
          <h2 style={styles.titre}>Choisissez votre espace</h2>
          <p style={styles.sousTitre}>
            Votre compte a plusieurs rôles. Sélectionnez celui avec lequel
            vous souhaitez continuer.
          </p>
          {erreur && <p style={styles.erreurTexte}>{erreur}</p>}
          <div style={styles.liste}>
            {roles.map((r) => (
              <button
                key={r.contextKey}
                style={styles.optionRole}
                disabled={switching}
                onClick={() => choisirRole(r)}
              >
                {libelleRole(r)}
              </button>
            ))}
          </div>
          <button onClick={logout} style={styles.lienDeconnexion}>
            Déconnexion
          </button>
        </div>
      </div>
    );
  }

  // ---- Mode "compact" : sélecteur discret dans le header ----
  if (roles.length <= 1) return null; // rien à switcher

  return (
    <div style={styles.compactWrapper}>
      <button style={styles.compactBouton} onClick={() => setOuvert((v) => !v)}>
        {activeRole ? libelleRole(activeRole) : 'Choisir un rôle'} ▾
      </button>
      {ouvert && (
        <div style={styles.compactMenu}>
          {roles.map((r) => (
            <button
              key={r.contextKey}
              style={{
                ...styles.compactOption,
                ...(activeRole?.contextKey === r.contextKey ? styles.compactOptionActive : {}),
              }}
              disabled={switching}
              onClick={() => choisirRole(r)}
            >
              {libelleRole(r)}
            </button>
          ))}
          {erreur && <p style={styles.erreurTexteCompact}>{erreur}</p>}
        </div>
      )}
    </div>
  );
}

const styles = {
  gateOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '32px 28px',
    width: 380,
    maxWidth: '90vw',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    textAlign: 'center',
  },
  titre: { margin: '0 0 8px', fontSize: 20, fontWeight: 600, color: '#111827' },
  sousTitre: { margin: '0 0 20px', fontSize: 14, color: '#6b7280' },
  liste: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionRole: {
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 500,
    color: '#111827',
    transition: 'background 0.15s',
  },
  lienDeconnexion: {
    marginTop: 20,
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontWeight: 700,
    cursor: 'pointer',
  },
  erreurTexte: { color: '#dc2626', fontSize: 13, margin: '0 0 12px' },
  erreurTexteCompact: { color: '#dc2626', fontSize: 12, margin: '8px 4px 0' },
  compactWrapper: { position: 'relative', display: 'inline-block' },
  compactBouton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
  },
  compactMenu: {
    position: 'absolute',
    top: '110%',
    right: 0,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    minWidth: 220,
    padding: 6,
    zIndex: 50,
  },
  compactOption: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    color: '#111827',
  },
  compactOptionActive: {
    background: '#eef2ff',
    fontWeight: 600,
  },
};